import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { ConfigProvider, Select, Table, Button, Modal, Spin, message, Input, Dropdown, Menu, Checkbox, AutoComplete, Badge, Tag } from "antd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import api from "../../../authentication/axiosInstance";
import styled from "styled-components";
import { Box, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DownOutlined } from '@ant-design/icons';
import { IconTractor, IconUser, IconLock, IconLockOpen } from "@tabler/icons-react";
import styles from '../loaders/EquipmentAllocationTable.module.css';
import { Theme, Table as RadixTable, DataList } from '@radix-ui/themes';
import { AllocationSetupContext } from '../../../context/AllocationSetupContext';
import { hasMultiRoleAssignment, MULTI_ROLE_ASSIGNMENT_HINT } from '../../../utils/staffPositions';
import { filterConfirmedAndSortSites, isSiteUnavailableInSetup } from '../siteTableUtils';
import StaffDetailsModal from './StaffDetailsModal';
import { OverviewMenu } from '../overview/AllocationOverview';
import StaffSwapModal from '../loaders/StaffSwapModal';
import useStaffSwap from '../loaders/useStaffSwap';


const { Option } = Select;

const getPdfExportErrorMessage = (error, fallbackMessage) => {
  if (error?.response?.status === 404) {
    return "The allocation setup is no longer available. Refresh and try again.";
  }
  if (error?.response?.status === 403) {
    return "You do not have permission to export this allocation setup.";
  }
  return error.response?.data?.message || fallbackMessage;
};

const sanitizeForFilename = (name) => {
  if (!name) return "Setup";
  return name.replace(/[\\/:*?"<>|\r\n\t]+/g, "").replace(/\s+/g, " ").trim() || "Setup";
};

const buildPdfFilename = (baseFilename, setupName) => {
  const safeName = sanitizeForFilename(setupName);
  const dotIndex = baseFilename.lastIndexOf(".");
  if (dotIndex === -1) return `${baseFilename} - ${safeName}`;
  return `${baseFilename.slice(0, dotIndex)} - ${safeName}${baseFilename.slice(dotIndex)}`;
};

const StaffCard = styled.div`
  background-color: #f0f0f0;
  padding: 10px;
  border-radius: 4px;
  cursor: grab;
  text-align: center;
  margin-bottom: 10px;
  width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StaffList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 300px;
  max-height: 650px; /* Set a fixed height */
  overflow-y: scroll; /* Enable vertical scrolling */
  margin: 0;
  padding: 0;
`;

const StaffAllocationPage = () => {
  const { refreshVersion, viewedSetup, loading: setupLoading, getSetupApiPath } = useContext(AllocationSetupContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [staffFilter, setStaffFilter] = useState('any');
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allocationMode, setAllocationMode] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const navigate = useNavigate();
  const MAX_EXPANDED_ROWS = 3;
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [unlockSiteId, setUnlockSiteId] = useState(null);
  const [password, setPassword] = useState("");
  const [isExportMenuVisible, setIsExportMenuVisible] = useState(false);
  const [selectedContractStatuses, setSelectedContractStatuses] = useState(['Estimate', 'Unconfirmed', 'Confirmed']);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [staffPositions, setStaffPositions] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [staffStatus, setStaffStatus] = useState("idle");
  const [sitesStatus, setSitesStatus] = useState("idle");
  const [isDeallocatingStaff, setIsDeallocatingStaff] = useState(false);
  const [isAllocatingStaff, setIsAllocatingStaff] = useState(false);
  const staffMutationRef = useRef(false);
  const [detailsRefreshWarning, setDetailsRefreshWarning] = useState("");

  const [searchText, setSearchText] = useState("");
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const selectedSetupId = viewedSetup?.id;
  const isLockedHistory = viewedSetup?.status === "archived";
  const selectedSetupIdRef = useRef(selectedSetupId);
  const isSetupInitializing = setupLoading && !selectedSetupId;
  const hasNoSetup = !setupLoading && !selectedSetupId;

  const handleSearch = (value) => {
    setSearchText(value);
  
    const filtered = staff.filter((member) =>
      member.name.toLowerCase().includes(value.toLowerCase())
    );
  
    setFilteredStaff(filtered);
  
    setAutoCompleteOptions(
      filtered.map((staff) => ({
        value: staff.name,
        label: staff.name,
        key: staff.staff_id,
      }))
    );
  };

  const handleSelect = (value) => {
    const selectedStaff = filteredStaff.find(staff => staff.name === value);
    if (selectedStaff) {
      setFilteredStaff([selectedStaff]); // Set the filteredStaff to just the selected one
    }
  };

  const StaffDragCard = ({ staff, allocationMode, onOpenDetails, onSwap, swapDisabled }) => {
    const isAllocated = staff.assigned_bool || hasMultiRoleAssignment(staff);
    const canDragStaff = allocationMode && !isAllocated && !isLockedHistory && !staffActionsBusy;
    const loader = staff.assigned_loader;
    // Finalized sites are reported by the picker itself, from fresh loader data.
    const canSwap = Boolean(loader) && !staff.archived && !loader.archived && !isLockedHistory && Boolean(onSwap);
    const [{ isDragging }, drag] = useDrag({
      type: "staff",
      item: {
        id: staff.staff_id,
        type: "staff",
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: canDragStaff,
    });

    const handleClick = (e) => {
      e.preventDefault();
      onOpenDetails(staff.staff_id);
    };

    return (
      <>
        <StaffCard ref={allocationMode ? drag : null} style={{ opacity: isDragging ? 0.5 : 1,
          backgroundColor: isAllocated ? '#cce6cc' : '#f0f0f0',
          cursor: canDragStaff ? "grab" : "pointer"
          }}
          onClick={handleClick}
          title={canDragStaff
            ? "Drag to allocate or click to view details."
            : hasMultiRoleAssignment(staff) ? MULTI_ROLE_ASSIGNMENT_HINT : "Click to view details."}
        >
          <Box style={{ width: '100%', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', width: '100%' }}>
              <span style={{ fontWeight: '600', fontSize: '16px' }}><IconUser size={14} style={{ marginRight: '3px' }} />{staff.name}</span>
              {staff.archived && <Tag color="default">Unavailable</Tag>}
              <span style={{ fontSize: '14px', marginBottom: 4 }}>{staff.position}</span>       
              <span style={{ fontSize: '14px' }}>City: {staff.city ? staff.city : 'Unspecified'}</span>       
              <span style={{ fontSize: '14px' }}>Rating: {staff.staff_rating ? staff.staff_rating : 'Unspecified'}</span>       
              <span style={{ fontSize: '14px' }}>Overall Rating: {staff.staff_overall_rating ? staff.staff_overall_rating : 'Unspecified'}</span>       
            </div>
            <Box style={{ display: "flex", alignItems: "center", marginTop: '15px', marginBottom: "5px"}}>
              <Box style={{ flex: 1 }}>
                {isAllocated ? (
                  <div style={{ backgroundColor: "#034832", width: '100%', height: '4px', borderRadius: '10px' }} />
                ) : (
                  <div style={{ backgroundColor: "#9DB5AF", width: '100%', height: '4px', borderRadius: '10px' }} />
                )}
              </Box>
            </Box>
            <Box style={{fontSize:"12px", position: 'absolute', top: '0', right: '0', marginRight: '-5px', fontWeight: '500', color: "#010101"}}>
              {isAllocated ? (
                  <Tag style={{ backgroundColor: "#034832", color: "white", padding: '0px 15px' }}>Assigned</Tag>
                ) : (
                  <Tag style={{ padding: '0px 10px' }}>Assignable</Tag>
                )}
            </Box>
            {canSwap && (
              <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                <Button
                  size="small"
                  className={styles.siteModalSwapButton}
                  aria-label={`Swap ${staff.name}`}
                  title={`Swap ${staff.name} on ${loader.vehicle_name || 'this loader'}`}
                  disabled={swapDisabled}
                  onClick={(event) => {
                    // Keep the card's own click from opening Staff Details.
                    event.stopPropagation();
                    onSwap({ ...staff, vehicle_id: loader.vehicle_id, vehicle_name: loader.vehicle_name });
                  }}
                >
                  Swap
                </Button>
              </Box>
            )}
          </Box>
        </StaffCard>
      </>
    );
  };

  const staffPath = (suffix) => getSetupApiPath(`staff/${suffix}`);

  const fetchStaff = async ({ keepReadyOnError = false } = {}) => {
    const requestSetupId = selectedSetupId;
    setStaffStatus("loading");
    try{
        const response = await api.get(staffPath("staff/"));
        if (selectedSetupIdRef.current !== requestSetupId) return false;
        setStaff(response.data);
        setFilteredStaff(response.data);
        
        // Extract unique positions from staff data
        const positions = [...new Set(response.data.map(s => s.position))];
        setStaffPositions(positions);
        setStaffStatus("ready");
        return true;
    } catch (error) {
        if (selectedSetupIdRef.current !== requestSetupId) return false;
        console.error("Failed to fetch staff:", error);
        message.error("Failed to fetch staff");
        setStaffStatus(keepReadyOnError ? "ready" : "error");
        return false;
    }
  }

  const fetchSites = async () => {
    const requestSetupId = selectedSetupId;
    setLoading(true);
    setSitesStatus("loading");
    try {
      const response = await api.get(staffPath("sites/"));
      if (selectedSetupIdRef.current !== requestSetupId) return false;
      setSites(response.data);
      setSitesStatus("ready");
      return true;
    } catch (error) {
      if (selectedSetupIdRef.current !== requestSetupId) return false;
      console.error("Failed to fetch sites:", error);
      message.error("Failed to fetch sites");
      setSitesStatus("error");
      return false;
    } finally {
      if (selectedSetupIdRef.current === requestSetupId) setLoading(false);
    }
  };

  useEffect(() => {
    selectedSetupIdRef.current = selectedSetupId;
    setSelectedStaffId(null);
    setIsAllocatingStaff(false);
    setIsDeallocatingStaff(false);
    setDetailsRefreshWarning("");
    setStaff([]);
    setFilteredStaff([]);
    setSites([]);
    setStaffStatus(selectedSetupId ? "loading" : "idle");
    setSitesStatus(selectedSetupId ? "loading" : "idle");
  }, [selectedSetupId]);

  useEffect(() => {
    if (!selectedSetupId) return;
    fetchStaff();
    fetchSites();
  }, [refreshVersion, selectedSetupId]);

  // Client-side filtering
  useEffect(() => {
    let filtered = filterConfirmedAndSortSites(sites);

    if (selectedRegion !== "all") {
      filtered = filtered.filter((site) => site.region === selectedRegion);
    }

    setFilteredSites(filtered);
  }, [selectedRegion, sites, refreshVersion]);

  const allocateStaff = async (vehicleId, staff_id) => {
    if (staffMutationRef.current) return false;
    staffMutationRef.current = true;
    const operationSetupId = selectedSetupId;
    setIsAllocatingStaff(true);
    try {
      await api.post(
        staffPath(`vehicles/${vehicleId}/allocate/`),
        { staff_id: staff_id }
      ).then(response => {
        message.success(response.data.message);
      });
      if (selectedSetupIdRef.current !== operationSetupId) return true;
      await Promise.all([fetchStaff(), fetchSites()]);
      return true;
    } catch (error) {
      if (selectedSetupIdRef.current !== operationSetupId) return false;
      await fetchStaff();
      console.error("Failed to allocate Staff:", error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || error.message);
      return false;
    } finally {
      staffMutationRef.current = false;
      if (selectedSetupIdRef.current === operationSetupId) setIsAllocatingStaff(false);
    }
  };

  const deallocateStaff = async (vehicleId, staff_id, { fromDetails = false } = {}) => {
    if (staffMutationRef.current) return false;
    staffMutationRef.current = true;
    const operationSetupId = selectedSetupId;
    setIsDeallocatingStaff(true);
    if (fromDetails) {
      setDetailsRefreshWarning("");
    }
    try {
      const response = await api.post(
        staffPath(`vehicles/${vehicleId}/deallocate/`),
        { staff_id: staff_id }
      );
      message.success(response.data.message);

      if (selectedSetupIdRef.current !== operationSetupId) return true;

      if (fromDetails) {
        const patchStaffMember = (member) => member.staff_id === staff_id
          ? { ...member, assigned_bool: false, assigned_loader: null }
          : member;
        setStaff((currentStaff) => currentStaff.map(patchStaffMember));
        setFilteredStaff((currentStaff) => currentStaff.map(patchStaffMember));
        setSites((currentSites) => currentSites.map((site) => ({
          ...site,
          vehicles_lots: (site.vehicles_lots || []).map((vehicle) => (
            vehicle.vehicle_id === vehicleId
              ? {
                ...vehicle,
                assigned_staff: (vehicle.assigned_staff || []).filter((member) => member.staff_id !== staff_id),
              }
              : vehicle
          )),
        })));
      }

      const [staffRefreshed, sitesRefreshed] = await Promise.all([
        fetchStaff({ keepReadyOnError: fromDetails }),
        fetchSites(),
      ]);
      if (selectedSetupIdRef.current === operationSetupId && fromDetails && (!staffRefreshed || !sitesRefreshed)) {
        setDetailsRefreshWarning("Assignment removed, but the latest allocation data couldn't be refreshed.");
      }
      return true;
    } catch (error) {
      console.error("Failed to deallocate Staff:", error.response?.data?.message || error.message);
      message.error(error.response?.data?.message || error.message);
      return false;
    } finally {
      staffMutationRef.current = false;
      if (selectedSetupIdRef.current === operationSetupId) setIsDeallocatingStaff(false);
    }
  }

  const {
    swapSource,
    openSwap,
    closeSwap,
    swapping: isSwappingStaff,
    loadSwapData,
    swapStaff,
  } = useStaffSwap({
    getSetupApiPath,
    selectedSetupId,
    selectedSetupIdRef,
    mutationRef: staffMutationRef,
    setRefreshWarning: setDetailsRefreshWarning,
    applySwap: (data, source, replacement) => {
      // The outgoing person takes the replacement's loader (or none); the replacement takes theirs.
      const nextLoaders = new Map([
        [source.staff_id, replacement.assigned_loader || null],
        [replacement.staff_id, source.assigned_loader || null],
      ]);
      const patchStaffMember = (member) => (nextLoaders.has(member.staff_id)
        ? {
          ...member,
          assigned_bool: Boolean(nextLoaders.get(member.staff_id)),
          assigned_loader: nextLoaders.get(member.staff_id),
        }
        : member);
      const updatedVehicles = new Map(
        (data.vehicles || []).map((vehicle) => [vehicle.vehicle_id, vehicle]),
      );
      setStaff((currentStaff) => currentStaff.map(patchStaffMember));
      setFilteredStaff((currentStaff) => currentStaff.map(patchStaffMember));
      setSites((currentSites) => currentSites.map((site) => ({
        ...site,
        vehicles_lots: (site.vehicles_lots || []).map((vehicle) => (
          updatedVehicles.has(vehicle.vehicle_id)
            ? { ...vehicle, assigned_staff: updatedVehicles.get(vehicle.vehicle_id).assigned_staff }
            : vehicle
        )),
      })));
    },
    refreshAfterSwap: async () => {
      const refreshed = await Promise.all([
        fetchStaff({ keepReadyOnError: true }),
        fetchSites(),
      ]);
      return refreshed.every(Boolean);
    },
  });

  const staffActionsBusy = isAllocatingStaff || isDeallocatingStaff || isSwappingStaff;

  const generateStaffPDF = async () => {
    try {
      const statusParams = selectedContractStatuses.map(status => `status=${encodeURIComponent(status)}`).join('&');
      const response = await api.get(`${staffPath('pdf/')}?${statusParams}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', buildPdfFilename('Staff_report.pdf', viewedSetup?.name));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Staff PDF generated successfully");
      setIsExportMenuVisible(false);  // Close the dropdown after generating PDF
    } catch (error) {
      console.error("Failed to generate staff pdf:", error);
      message.error(getPdfExportErrorMessage(error, "Failed to generate staff pdf"));
    }
  };

  const exportMenu = (
    <Menu>
      <Menu.SubMenu key="1" title="Export Staff Allocation">
        <Menu.ItemGroup>
          <div style={{ padding: '8px 16px' }}>
            <Checkbox.Group
              options={[
                { label: 'Estimate', value: 'Estimate' },
                { label: 'Unconfirmed', value: 'Unconfirmed' },
                { label: 'Confirmed', value: 'Confirmed' },
              ]}
              value={selectedContractStatuses}
              onChange={setSelectedContractStatuses}
            />
          </div>
          <div style={{ padding: '8px 16px' }}>
            <Button 
              onClick={generateStaffPDF} 
              disabled={selectedContractStatuses.length === 0}
              type="primary"
            >
              Generate PDF
            </Button>
          </div>
        </Menu.ItemGroup>
      </Menu.SubMenu>
    </Menu>
  );

  const showProjectDetails = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setSelectedProject(null);
  };

  const handleStaffTypeFilterChange = (value) => {
    setStaffFilter(value);
    if (value === "any") {
      setFilteredStaff(staff);
    } else {
      setFilteredStaff(staff.filter((s) => s.position === value));
    }
  };

  const finalizeStaffAllocation = async (siteId) => {
    try {
      await api.post(staffPath(`sites/${siteId}/finalize/`));
      message.success("Staff allocation finalized successfully");
      if (expandedRowKeys.includes(siteId)) {
        setExpandedRowKeys(prevKeys => prevKeys.filter(key => key !== siteId));
        if (expandedRowKeys.length === 1) {
          setAllocationMode(false);
        }
      }
      fetchSites();
    } catch (error) {
      console.error("Failed to finalize staff allocation:", error);
      message.error("Failed to finalize staff allocation");
    }
  };

  const UnlockStaffAllocation = async (siteId, password) => {
    try {
      await api.post(staffPath(`sites/${siteId}/unlock/`), {
        password,  // Pass the password in the request body
      });
      message.success("Staff allocation unlocked successfully");
      // Update the local state to reflect the change
      fetchSites();
    } catch (error) {
      console.error("Failed to unlock staff allocation:", error);
      message.error("Failed to unlock staff allocation");
    }
  };

  const handleRegionSelect = (value) => {
    setSelectedRegion(value);
    // No need to call fetchSites here as filtering is now client-side
  };

  const confirmedRegions = Array.from(new Set(filterConfirmedAndSortSites(sites).map((site) => site.region))).filter(Boolean);
  const selectedStaff = useMemo(
    () => staff.find((member) => member.staff_id === selectedStaffId) || null,
    [staff, selectedStaffId],
  );
  const assignedSites = useMemo(() => {
    const vehicleId = selectedStaff?.assigned_loader?.vehicle_id;
    if (!vehicleId) return [];

    return sites.flatMap((site) => (
      (site.vehicles_lots || [])
        .filter((vehicle) => vehicle.vehicle_id === vehicleId)
        .map((vehicle) => ({
          ...site,
          ...vehicle,
          site_id: site.site_id,
          vehicle_id: vehicle.vehicle_id,
          archived: Boolean(site.archived),
          loader_allocation_finalized: Boolean(site.loader_allocation_finalized),
          staff_allocation_finalized: Boolean(site.staff_allocation_finalized),
        }))
    ));
  }, [selectedStaff, sites]);

  const openStaffDetails = (staffId) => {
    setDetailsRefreshWarning("");
    setSelectedStaffId(staffId);
  };

  const closeStaffDetails = () => {
    setSelectedStaffId(null);
    closeSwap();
    setDetailsRefreshWarning("");
  };

  const columns = [
    { 
      title: "Customer Name", 
      dataIndex: "client_name", 
      key: "client_name",
      width: 150
    },
    { 
      title: "Site Name", 
      dataIndex: "site_name", 
      key: "site_name",
      width: 175,
      render: (name, record) => <span>{name} {isSiteUnavailableInSetup(record) && <Tag color="default">Unavailable</Tag>}</span>,
    },
    {
      title: "Vehicles Allocated",
      dataIndex: "vehicles_lots",
      key: "vehicles_allocated",
      width: 200,
      render: (vehicles_lots, record) => (
        <ul style={{ listStyleType: "none", paddingLeft: 0, margin: 0 }}>
          {vehicles_lots.length === 0 ? (
            <li>N/A</li>
          ) : (
            vehicles_lots.map(vehicle => (
              <li key={vehicle.vehicle_id}>
                {vehicle.vehicle_subtype}, {vehicle.vehicle_name}
              </li>
            ))
          )}
        </ul>
      )
    },
    {
      title: "Lots Area (sq. ft)",
      dataIndex: "parking_lots_sqft",
      key: "parking_lots_sqft",
      width: 240,
      render: (lotsArea, record) => {
        const allocationPercentage = (record.parking_lots_sqft_allocated / lotsArea) * 100;
        const displayPercentage = allocationPercentage > 100 ? 100 : allocationPercentage;

        return (
          <span>
            {record.parking_lots_sqft_allocated.toLocaleString()} / {lotsArea.toLocaleString()}
            <Box style={{ display: "flex", alignItems: "center", width: 200 }}>
              <LinearProgress 
                style={{ flex: 9, marginRight: 8 }} 
                variant="determinate" 
                value={displayPercentage}
              />
              <Box style={{ flex: 1 }}>
                {allocationPercentage.toFixed(1)}%
              </Box>
            </Box>
            {allocationPercentage > 100 && (
              <Box style={{ color: 'Orange', marginTop: 4 }}>
                Over Allocated
              </Box>
            )}
          </span>
        )
      },
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 120
    },
    {
      title: "Locked",
      key: "locked",
      width: 81,
      render: (_, record) => isLockedHistory ? null : (
        <Box>
        {record.staff_allocation_finalized ? (
          <div style={{ backgroundColor: '#E5F2EC', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
            <IconLock size={18} stroke={2} />
          </div>
          
        ) : (
          <div style={{ backgroundColor: '#f5f5f5', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ebeaea', borderRadius: '4px', color: "#034832" }}>
            <IconLockOpen size={18} stroke={2} />
          </div>
        )}
      </Box>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Box style={{ display: "flex", alignItems: "flex-start" }}>
          <div
              className={styles.tableButtonGold}
              style={{ width: '135px' }}
              onClick={() => {
                if (record.staff_allocation_finalized) {
                  // If allocation is locked, show password modal
                  setUnlockSiteId(record.site_id);
                  setPasswordModalVisible(true);
                } else {
                  // Finalize allocation if it's not locked
                  finalizeStaffAllocation(record.site_id);
                }
              }}
            >
              {record.staff_allocation_finalized ? "Unlock Finalization" : "Finalize Allocation"}
            </div>
        </Box>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    return (
      <Box style={{ marginBottom: "8px" }}>
        <p style={{ fontWeight: "bold", marginBottom: "4px", fontSize: "16px" }}>Allocated Loaders:</p>
        {record.vehicles_lots?.length > 0 ? (
          <RadixTable.Root size="1">
            <RadixTable.Header>
              <RadixTable.Row>
                {!isLockedHistory && <RadixTable.ColumnHeaderCell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Allocate Staff</RadixTable.ColumnHeaderCell>}
                <RadixTable.ColumnHeaderCell style={{ width: "30%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Vehicle</RadixTable.ColumnHeaderCell>
                <RadixTable.ColumnHeaderCell style={{ width: "30%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Assigned Staff</RadixTable.ColumnHeaderCell>
                {!isLockedHistory && <RadixTable.ColumnHeaderCell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Actions</RadixTable.ColumnHeaderCell>}
              </RadixTable.Row>
            </RadixTable.Header>
  
            <RadixTable.Body>
              {record.vehicles_lots.map((vehicle) => (
                <RadixTable.Row key={vehicle.vehicle_id}>
                  {!isLockedHistory && <RadixTable.Cell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "14px" }}>
                    <AllocateStaffCell
                      vehicle={vehicle}
                      allocateStaff={allocateStaff}
                    />
                  </RadixTable.Cell>}
                  <RadixTable.Cell style={{ width: "30%", textAlign: "left", padding: "4px 8px", fontSize: "14px" }}>
                    <strong>{vehicle.vehicle_name}</strong> ({vehicle.vehicle_subtype})
                  </RadixTable.Cell>
                  <RadixTable.Cell style={{ width: "30%", textAlign: "left", padding: "4px 8px", fontSize: "14px" }}>
                    {vehicle.assigned_staff && vehicle.assigned_staff.length > 0 ? (
                      <ul style={{ paddingLeft: 0, margin: 0 }}>
                        {vehicle.assigned_staff.map((staff) => (
                          <li key={staff.staff_id} style={{ listStyle: "none", marginBottom: "4px" }}>
                            <strong>{staff.name}</strong> - {staff.position}, Rating: {staff.staff_rating ? staff.staff_rating : "Unspecified"}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      "Unassigned"
                    )}
                  </RadixTable.Cell>
                  {!isLockedHistory && <RadixTable.Cell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "14px" }}>
                    {vehicle.assigned_staff && vehicle.assigned_staff.length > 0 ? (
                      vehicle.assigned_staff.map((staff) => (
                        <Button
                          key={staff.staff_id}
                          onClick={() => deallocateStaff(vehicle.vehicle_id, staff.staff_id)}
                          disabled={staffActionsBusy}
                          type="primary"
                          danger
                          size="small"
                          style={{ marginRight: "4px", height: 30, width: 150 }}
                        >
                          Deallocate
                        </Button>
                      ))
                    ) : (
                      <span></span>
                    )}
                  </RadixTable.Cell>}
                </RadixTable.Row>
              ))}
            </RadixTable.Body>
          </RadixTable.Root>
        ) : (
          <p style={{ fontSize: "14px" }}>No Loaders Allocated</p>
        )}
      </Box>
    );
  };

  const AllocateStaffCell = ({ vehicle, allocateStaff }) => {
    const [{ isOver }, drop] = useDrop({
      accept: "staff",
      canDrop: () => !staffMutationRef.current,
      drop: (item) => allocateStaff(vehicle.vehicle_id, item.id),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <Button
        ref={drop}
        disabled={staffActionsBusy}
        type="primary"
        style={{
          backgroundColor: isOver ? "#cdaf5e" : "#cdaf5e",
          borderColor: isOver ? "#cdaf5e" : "#cdaf5e",
          opacity: 1,
          width: 250,
          height: 50,
        }}
      >
        <strong>{isOver ? "Release to Allocate" : "Allocate Staff"}</strong>
      </Button>
    );
  };

  const onTableExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys((prevKeys) => {
        if (prevKeys.length < MAX_EXPANDED_ROWS) {
          setAllocationMode(!isLockedHistory);
          return [...prevKeys, record.site_id];
        }
        message.warning(`You can only expand up to ${MAX_EXPANDED_ROWS} sites at a time.`);
        return prevKeys;
      });
    } else {
      setExpandedRowKeys((prevKeys) => {
        const newKeys = prevKeys.filter((key) => key !== record.site_id);
        if (newKeys.length === 0) {
          setAllocationMode(false);
        }
        return newKeys;
      });
    }
  };

  if (isSetupInitializing) {
    return (
      <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, minHeight: 240 }}>
        <Spin />
        <span>Loading allocation setup...</span>
      </Box>
    );
  }

  if (hasNoSetup) {
    return (
      <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        No allocation setup available.
      </Box>
    );
  }

  return (
    <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832", controlItemBgActive: "#E4F2EB" } }}>
      <DndProvider backend={HTML5Backend}>
        <style>{`
          .ant-table-tbody > tr:hover > td {
            background: transparent !important; /* Removes hover background */
          }
          .locked-row {
            background-color: #f0f0f0 !important;
          }
        `}</style>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', justifyContent: 'center', padding: '20px',}}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '300px',
              }}
            >
                <span style={{fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '600', fontSize: '28px'}}>
                  Staff
                </span>
                {/* Add the staff filter select here */}
                <Select
                  style={{ width: 'calc(100% - 20px)' }}
                  value={staffFilter}
                  onChange={handleStaffTypeFilterChange}
                >
                  <Option value="any">All Positions</Option>
                  {staffPositions.map(position => (
                    <Option key={position} value={position}>{position}</Option>
                  ))}
                </Select>
                <Input
                  style={{ width: '100%' }}
                  onSearch={handleSearch}
                  onSelect={handleSelect}
                  placeholder="Search staff"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)} // use the debounced function or directly handleSearch
              />
                <StaffList>
                  {filteredStaff.map((staff) => (
                    <StaffDragCard
                      key={staff.staff_id}
                      staff={staff}
                      allocationMode={allocationMode && !isLockedHistory}
                      onOpenDetails={openStaffDetails}
                      onSwap={openSwap}
                      swapDisabled={staffActionsBusy}
                    />
                  ))}
                </StaffList>
            </div>
            <div style={{ width: 'calc(100% - 320px)', marginLeft: '20px'}}>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Select
                    placeholder="Select Region"
                    value={selectedRegion}
                    style={{ width: 150 }}
                    onChange={handleRegionSelect}
                  >
                    <Option value="all">All Regions</Option>
                    {confirmedRegions.map((region) => (
                      <Option key={region} value={region}>
                        {region.charAt(0).toUpperCase() + region.slice(1)}
                      </Option>
                    ))}
                  </Select>
                  <Dropdown overlay={exportMenu} trigger={['click']}>
                    <Button style={{ width: 150 }}>
                      PDF Export <DownOutlined />
                    </Button>
                  </Dropdown>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><div className={styles.tableButton} onClick={() => navigate('/allocation')} style={{ fontSize: '14px', padding: '8px 15px' }}>
                  <IconTractor size={16}/>
                  Loader Allocation
                </div>
                <OverviewMenu setupId={selectedSetupId} /></div>
              </div>
              <Spin spinning={loading}>
                <Table
                  dataSource={filteredSites}
                  columns={columns}
                  rowKey="site_id"
                  pagination={false}
                  scroll={{ y: 690 }}
                  rowClassName={(record) =>
                    record.staff_allocation_finalized ? 'locked-row' : ''
                  }
                  expandable={{
                    expandedRowRender,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (expanded, record) => {
                      if (isLockedHistory || !record.staff_allocation_finalized) {
                        onTableExpand(expanded, record);
                      }
                    },
                    expandIcon: ({ expanded, onExpand, record }) =>
                      !isLockedHistory && record.staff_allocation_finalized ? null : (
                        <Button
                          onClick={(e) => onExpand(record, e)}
                          style={{ border: 'none', backgroundColor: 'transparent' }}
                          disabled={!expanded && expandedRowKeys.length >= MAX_EXPANDED_ROWS}
                        >
                          {expanded ? '-' : '+'}
                        </Button>
                      ),
                  }}
                />
              </Spin>
              <Modal title="Vehicle Details" open={showModal} onCancel={handleModalCancel} footer={null}>
                {selectedProject && (
                  <div>
                    <p>Vehicle Name: {selectedProject.vehicle_name}</p>
                    <p>Make/Model: {selectedProject.vehicle_make}, {selectedProject.vehicle_model}</p>
                    <p>Surface Type: {selectedProject.surface_type}</p>
                    <p>Max Capacity: {selectedProject.vehicle_capacity.toLocaleString()} sq. ft</p>
                    <p>Remaining Capacity: {selectedProject.remaining_capacity.toLocaleString()} sq. ft</p>
                    <br></br>
                    <p>Assigned Staff:</p>
                    {selectedProject.assigned_staff.length > 0 ? (
                      <ul>
                        {selectedProject.assigned_staff.map((staff) => (
                          <li key={staff.staff_id}>{staff.name} - {staff.position}</li>
                        ))}
                      </ul>
                    ) : (
                      <ul><li>No Staff Assigned</li></ul>
                    )}
                  </div>
                )}
              </Modal>
              <StaffDetailsModal
                open={selectedStaffId !== null}
                staff={selectedStaff}
                assignedSites={assignedSites}
                staffStatus={staffStatus}
                sitesStatus={sitesStatus}
                setupName={viewedSetup?.name}
                refreshWarning={detailsRefreshWarning}
                onClose={closeStaffDetails}
                onRetryStaff={() => fetchStaff()}
                onRetrySites={fetchSites}
                onRetryAll={() => Promise.all([fetchStaff(), fetchSites()]).then(([staffOk, sitesOk]) => {
                  if (staffOk && sitesOk) setDetailsRefreshWarning("");
                })}
                onDeallocate={(vehicleId, staffId) => deallocateStaff(
                  vehicleId,
                  staffId,
                  { fromDetails: true },
                )}
                isDeallocating={isDeallocatingStaff}
                onSwap={openSwap}
                isSwapping={isSwappingStaff || isAllocatingStaff}
                readOnly={isLockedHistory}
              />
              {/* Opened from Staff Details or directly from a staff card. */}
              {swapSource && !isLockedHistory && (
                <StaffSwapModal
                  key={`${selectedSetupId}-${swapSource.staff_id}-${swapSource.vehicle_id}`}
                  source={swapSource}
                  loadData={loadSwapData}
                  onSwap={swapStaff}
                  onClose={closeSwap}
                />
              )}
            </div>
          </div>
        <Modal
          title="Enter Password to Unlock"
          open={passwordModalVisible}
          onCancel={() => setPasswordModalVisible(false)}
          onOk={async () => {
            if (password) {
              await UnlockStaffAllocation(unlockSiteId, password);
              setPasswordModalVisible(false);
              setPassword("");
            } else {
              message.error("Please enter a password");
            }
          }}
        >
          <Input.Password
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Modal>
      </DndProvider>
    </ConfigProvider>
  );
};

export default StaffAllocationPage;
